/**
 * CMS-07 — heroes table plus the editing drawer: name, type (song / playlist), target picker
 * and the two posters with their previews at the real aspect ratios (TV 1920×720, mobile
 * 1080×1350, docs/06 §2.6).
 *
 * A hero is created first and its posters uploaded afterwards, because the R2 key contains the
 * hero id (`catalog-images/heroes/{id}/…`, docs/02 §3). Publishing is refused by the API until
 * both posters exist and the target is published, and the drawer says which piece is missing.
 */
import {
  type CmsHeroDTO,
  type CmsHeroesQueryInput,
  type CmsHeroesResponse,
  type CmsPlaylistsResponse,
  type CmsSongsResponse,
  HERO_POSTER_MOBILE,
  HERO_POSTER_TV,
  type HeroPublishRequirement,
  type HeroType,
  type PublishStatus,
} from '@akpl/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { type ChangeEvent, useMemo, useRef, useState } from 'react';

import { api } from '../api';
import { atLeast, useAuth } from '../auth/AuthProvider';
import { DataTable, PAGE_SIZE } from '../components/data-table/DataTable';
import { EntitySearch, type SearchCandidate } from '../components/editorial/EntitySearch';
import { swatch } from '../components/editorial/OrderedList';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/button';
import { useConfirm } from '../components/ui/confirm';
import { Drawer } from '../components/ui/drawer';
import { Field, Input, Label, Select } from '../components/ui/field';
import { Badge } from '../components/ui/panel';
import { useT } from '../i18n';
import type { CsvColumn } from '../lib/csv';
import { fetchAllRowsResult } from '../lib/exportAll';
import { uploadFile } from '../lib/upload';

export interface HeroFilters {
  q: string;
  status: PublishStatus | '';
  type: HeroType | '';
}

export const EMPTY_HERO_FILTERS: HeroFilters = { q: '', status: '', type: '' };

const SORT_IDS = new Set(['name', 'status', 'created_at', 'updated_at']);

export function toHeroesQuery(
  filters: HeroFilters,
  page: number,
  sorting: SortingState,
): CmsHeroesQueryInput {
  const query: CmsHeroesQueryInput = { page, pageSize: PAGE_SIZE };
  if (filters.q.trim()) query.q = filters.q.trim();
  if (filters.status) query.status = filters.status;
  if (filters.type) query.type = filters.type;
  const sort = sorting[0];
  if (sort && SORT_IDS.has(sort.id)) {
    query.sort = sort.id;
    query.dir = sort.desc ? 'desc' : 'asc';
  }
  return query;
}

/** Labels of the `409 conflict { missing }` the publish gate answers with (docs/06 §2.6). */
function missingLabels(t: ReturnType<typeof useT>['t']): Record<HeroPublishRequirement, string> {
  return {
    posterTv: t('cms:heroes.missing_posterTv'),
    posterMobile: t('cms:heroes.missing_posterMobile'),
    target: t('cms:heroes.missing_target'),
  };
}

/** The poster boxes of CMS-07: real aspect ratio, flat placeholder while empty. */
function PosterPreview({
  url,
  width,
  height,
  boxWidth,
  label,
  seed,
}: {
  url: string | null;
  width: number;
  height: number;
  boxWidth: number;
  label: string;
  seed: string;
}) {
  const boxHeight = Math.round((boxWidth * height) / width);
  return (
    <div
      className="relative flex items-end overflow-hidden rounded-lg"
      style={{ width: boxWidth, height: boxHeight, background: swatch(seed) }}
      data-testid={`poster-${label}`}
    >
      {url ? (
        <img src={url} alt="" className="absolute inset-0 size-full object-cover" />
      ) : (
        <span className="relative p-3 text-[12px] font-semibold text-white/90">{label}</span>
      )}
    </div>
  );
}

function HeroDrawer({ hero, onClose }: { hero: CmsHeroDTO | 'new'; onClose: () => void }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const isNew = hero === 'new';
  const current = isNew ? null : hero;

  const [draft, setDraft] = useState(() => ({
    name: current?.name ?? '',
    type: current?.type ?? ('song' as HeroType),
    refId: current?.refId ?? '',
    refTitle: current?.refTitle ?? '',
    posterTvPath: current?.posterTvPath ?? null,
    posterMobilePath: current?.posterMobilePath ?? null,
    status: current?.status ?? ('draft' as PublishStatus),
  }));
  const [error, setError] = useState<string | null>(null);
  const tvInput = useRef<HTMLInputElement>(null);
  const mobileInput = useRef<HTMLInputElement>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['cms', 'heroes'] });

  const searchTargets = async (term: string): Promise<SearchCandidate[]> => {
    if (draft.type === 'playlist') {
      const page: CmsPlaylistsResponse = await api.cmsPlaylists({
        query: { q: term, pageSize: 8 },
      });
      return page.items.map((playlist) => ({
        id: playlist.id,
        title: playlist.name.es || playlist.name.en,
        subtitle: t('cms:playlists.songsCount', { count: playlist.total }),
        imageUrl: playlist.imageUrl,
        tag: playlist.status === 'published' ? undefined : t('cms:status.draft'),
      }));
    }
    const page: CmsSongsResponse = await api.cmsSongs({ query: { q: term, pageSize: 8 } });
    return page.items.map((song) => ({
      id: song.id,
      title: song.title,
      subtitle: song.artists.map((artist) => artist.name).join(', '),
      imageUrl: song.imageUrl,
      tag: song.status === 'published' ? undefined : t('cms:status.draft'),
    }));
  };

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: draft.name.trim(),
        type: draft.type,
        refId: draft.refId,
        posterTvPath: draft.posterTvPath,
        posterMobilePath: draft.posterMobilePath,
        status: draft.status,
      };
      // CMS-36: the drawer offers "Published", so it has to POST it — dropping it silently
      // created a draft and closed as if the hero were live.
      if (isNew) return api.cmsHeroCreate({ body });
      return api.cmsHeroPatch({ params: { id: current!.id }, body });
    },
    onSuccess: () => {
      void invalidate();
      onClose();
    },
    onError: (err: unknown) => {
      const details = (err as { details?: { missing?: string[] } }).details;
      const missing = details?.missing
        ?.map((key) => missingLabels(t)[key as HeroPublishRequirement])
        .filter(Boolean);
      setError(
        missing && missing.length > 0
          ? t('cms:heroes.publishBlocked', { missing: missing.join(', ') })
          : t('common:states.error'),
      );
    },
  });

  const confirm = useConfirm();

  const remove = useMutation({
    mutationFn: () => api.cmsHeroDelete({ params: { id: current!.id } }),
    onSuccess: () => {
      void invalidate();
      onClose();
    },
    onError: () => setError(t('cms:heroes.deleteBlocked')),
  });

  /** A hero is what the TV opens on, so deleting it asks first (UX-211). */
  const onDelete = async () => {
    const ok = await confirm({
      title: t('cms:confirm.deleteHero.title', { name: current!.name }),
      body: t('cms:confirm.deleteHero.body'),
      confirmLabel: t('cms:confirm.deleteHero.confirm'),
      tone: 'danger',
    });
    if (ok) remove.mutate();
  };

  const onPoster = async (event: ChangeEvent<HTMLInputElement>, which: 'tv' | 'mobile') => {
    const file = event.target.files?.[0];
    if (!file || isNew) return;
    try {
      const { key } = await uploadFile(file, {
        kind: 'image',
        owner: 'hero',
        ownerId: current!.id,
      });
      setDraft((d) =>
        which === 'tv' ? { ...d, posterTvPath: key } : { ...d, posterMobilePath: key },
      );
    } catch {
      setError(t('cms:songs.editor.uploadFailed'));
    }
  };

  const labels = missingLabels(t);
  const missing = [
    ...(draft.posterTvPath ? [] : [labels.posterTv]),
    ...(draft.posterMobilePath ? [] : [labels.posterMobile]),
    ...(current && !current.refPublished ? [labels.target] : []),
  ];
  /**
   * CMS-36: the hint used to be hidden while `isNew`, so an operator who picked "Published"
   * on a new hero saw nothing. A brand-new hero has no posters yet (they are uploaded once the
   * id exists), so publishing at create time is always refused with a 409 — say so up front.
   */
  const publishBlocked = draft.status === 'published' && missing.length > 0;
  // Only a *new* hero is stopped: an existing one still saves its other fields and lets the
  // API answer, which is how the gate has always behaved.
  const createBlocked = isNew && publishBlocked;

  return (
    <Drawer
      open
      onClose={onClose}
      title={isNew ? t('cms:heroes.create') : `${t('cms:heroes.edit')} · ${current!.name}`}
      subtitle={isNew ? undefined : t('cms:heroes.meta', { id: current!.id })}
      footer={
        <>
          {!isNew && (
            <Button
              variant="link"
              size="sm"
              className="mr-auto text-danger"
              disabled={remove.isPending}
              onClick={() => void onDelete()}
            >
              {t('common:actions.delete')}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            data-testid="hero-save"
            disabled={!draft.name.trim() || !draft.refId || save.isPending || createBlocked}
            onClick={() => save.mutate()}
          >
            {t('cms:heroes.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t('cms:heroes.name')}>
          {(id) => (
            <Input
              id={id}
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('cms:heroes.type')}>
            {(id) => (
              <Select
                id={id}
                value={draft.type}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    type: e.target.value as HeroType,
                    refId: '',
                    refTitle: '',
                  }))
                }
              >
                <option value="song">{t('cms:heroes.typeSong')}</option>
                <option value="playlist">{t('cms:heroes.typePlaylist')}</option>
              </Select>
            )}
          </Field>
          <Field label={t('cms:heroes.status')}>
            {(id) => (
              <Select
                id={id}
                value={draft.status}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, status: e.target.value as PublishStatus }))
                }
              >
                <option value="draft">{t('cms:status.draft')}</option>
                <option value="published">{t('cms:status.published')}</option>
              </Select>
            )}
          </Field>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="hero-target">{t('cms:heroes.target')}</Label>
          {draft.refId ? (
            <div className="flex items-center gap-2 rounded-lg border border-line px-2 py-1.5">
              <span
                aria-hidden="true"
                className="size-8 rounded-md"
                style={{ background: swatch(draft.refId) }}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold text-ink">
                  {draft.refTitle || draft.refId}
                </span>
                <span className="block truncate text-[12px] text-muted">{draft.refId}</span>
              </span>
              <Button
                variant="link"
                size="sm"
                onClick={() => setDraft((d) => ({ ...d, refId: '', refTitle: '' }))}
              >
                {t('cms:heroes.change')}
              </Button>
            </div>
          ) : (
            <EntitySearch
              kind={`hero-target-${draft.type}`}
              placeholder={t('cms:heroes.searchTarget')}
              search={searchTargets}
              exclude={[]}
              onPick={(candidate) =>
                setDraft((d) => ({ ...d, refId: candidate.id, refTitle: candidate.title }))
              }
            />
          )}
        </div>

        <div className="flex flex-col gap-2 pt-2">
          <span className="flex items-center gap-2">
            <h3 className="text-[13px] font-bold text-ink">{t('cms:heroes.posterTv')}</h3>
            {draft.posterTvPath && <Badge tone="ok">{t('cms:heroes.uploaded')}</Badge>}
          </span>
          <PosterPreview
            url={current?.posterTvUrl ?? null}
            width={HERO_POSTER_TV.width}
            height={HERO_POSTER_TV.height}
            boxWidth={380}
            label={draft.name || t('cms:heroes.posterTv')}
            seed={current?.id ?? 'new-tv'}
          />
          <input
            ref={tvInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            className="hidden"
            data-testid="hero-tv-input"
            onChange={(event) => void onPoster(event, 'tv')}
          />
          <span className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={isNew}
              onClick={() => tvInput.current?.click()}
            >
              {t('cms:heroes.uploadTv')}
            </Button>
            <span className="truncate text-[12px] text-muted">{draft.posterTvPath ?? '—'}</span>
          </span>
        </div>

        <div className="flex flex-col gap-2 pt-2">
          <span className="flex items-center gap-2">
            <h3 className="text-[13px] font-bold text-ink">{t('cms:heroes.posterMobile')}</h3>
            {draft.posterMobilePath && <Badge tone="ok">{t('cms:heroes.uploaded')}</Badge>}
          </span>
          <div className="flex items-start gap-3">
            <PosterPreview
              url={current?.posterMobileUrl ?? null}
              width={HERO_POSTER_MOBILE.width}
              height={HERO_POSTER_MOBILE.height}
              boxWidth={140}
              label={draft.name || t('cms:heroes.posterMobile')}
              seed={current?.id ?? 'new-mobile'}
            />
            <div className="flex flex-1 flex-col gap-2">
              <p className="text-[12px] text-muted">{t('cms:heroes.posterHelp')}</p>
              <input
                ref={mobileInput}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                className="hidden"
                data-testid="hero-mobile-input"
                onChange={(event) => void onPoster(event, 'mobile')}
              />
              <Button
                variant="secondary"
                size="sm"
                disabled={isNew}
                onClick={() => mobileInput.current?.click()}
              >
                {t('cms:heroes.uploadMobile')}
              </Button>
              <span className="truncate text-[12px] text-muted">
                {draft.posterMobilePath ?? '—'}
              </span>
            </div>
          </div>
        </div>

        {isNew && <p className="text-[12px] text-muted">{t('cms:heroes.createHelp')}</p>}
        {(publishBlocked || (!isNew && missing.length > 0)) && (
          <p
            className="rounded-lg bg-warn/12 px-3 py-2 text-[12px] text-warn-ink"
            data-testid="hero-missing"
          >
            {t('cms:heroes.publishBlocked', { missing: missing.join(', ') })}
          </p>
        )}
        {error && (
          <p className="text-[12px] text-danger" role="alert">
            {error}
          </p>
        )}
      </div>
    </Drawer>
  );
}

export function Heroes() {
  const { t } = useT();
  const { role } = useAuth();
  const canEdit = atLeast(role, 'editor');

  const [filters, setFilters] = useState<HeroFilters>(EMPTY_HERO_FILTERS);
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'updated_at', desc: true }]);
  const [selected, setSelected] = useState<CmsHeroDTO | 'new' | null>(null);

  const query = toHeroesQuery(filters, page, sorting);
  const result = useQuery<CmsHeroesResponse>({
    queryKey: ['cms', 'heroes', query],
    queryFn: () => api.cmsHeroes({ query }),
    placeholderData: keepPreviousData,
  });

  const rows = result.data?.items ?? [];

  const columns = useMemo<ColumnDef<CmsHeroDTO>[]>(
    () => [
      {
        id: 'name',
        header: t('cms:heroes.heroColumn'),
        cell: ({ row }) => (
          <span className="flex items-center gap-3">
            {row.original.posterTvUrl ? (
              <img
                src={row.original.posterTvUrl}
                alt=""
                className="h-8 w-[72px] rounded-md object-cover"
              />
            ) : (
              <span
                aria-hidden="true"
                className="h-8 w-[72px] rounded-md"
                style={{ background: swatch(row.original.id) }}
              />
            )}
            <span className="min-w-0">
              <span className="block truncate font-semibold text-ink">{row.original.name}</span>
              <span className="block truncate text-[12px] text-muted">
                {row.original.refTitle || row.original.refId}
              </span>
            </span>
          </span>
        ),
      },
      {
        id: 'type',
        header: t('cms:heroes.type'),
        cell: ({ row }) => (
          <Badge tone={row.original.type === 'playlist' ? 'cyan' : 'pink'}>
            {t(`cms:heroes.type${row.original.type === 'playlist' ? 'Playlist' : 'Song'}`)}
          </Badge>
        ),
      },
      {
        id: 'posters',
        header: t('cms:heroes.posters'),
        cell: ({ row }) => (
          <span className="text-[12px] text-muted">
            {[
              row.original.posterTvPath ? t('cms:heroes.posterTvShort') : null,
              row.original.posterMobilePath ? t('cms:heroes.posterMobileShort') : null,
            ]
              .filter(Boolean)
              .join(' · ') || t('cms:heroes.noPosters')}
          </span>
        ),
      },
      {
        id: 'status',
        header: t('cms:heroes.status'),
        cell: ({ row }) => (
          <span className="flex items-center gap-1.5">
            <Badge tone={row.original.status === 'published' ? 'ok' : 'neutral'}>
              {t(`cms:status.${row.original.status}`)}
            </Badge>
            {!row.original.refPublished && (
              <Badge tone="warn">{t('cms:heroes.targetUnpublished')}</Badge>
            )}
          </span>
        ),
      },
    ],
    [t],
  );

  const csvColumns: CsvColumn<CmsHeroDTO>[] = [
    { header: 'id', value: (h) => h.id },
    { header: 'name', value: (h) => h.name },
    { header: 'type', value: (h) => h.type },
    { header: 'ref_id', value: (h) => h.refId },
    { header: 'ref_title', value: (h) => h.refTitle },
    { header: 'status', value: (h) => h.status },
    { header: 'poster_tv', value: (h) => h.posterTvPath ?? '' },
    { header: 'poster_mobile', value: (h) => h.posterMobilePath ?? '' },
  ];

  const update = (patch: Partial<HeroFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  return (
    <>
      <PageHeader
        title={t('cms:heroes.title')}
        /**
         * No count until the query answers: `total ?? 0` claimed an empty collection for the
         * seconds the first page takes (QA 2026-09-15 UX-502, the UX-201 rule applied to the
         * screens it was missed on). An error degrades the count to "—".
         */
        subtitle={
          result.data !== undefined
            ? t('cms:heroes.subtitle', { total: result.data.total })
            : result.isError
              ? t('cms:heroes.subtitle', { total: '—' })
              : t('cms:heroes.subtitleLoading')
        }
        actions={
          canEdit ? (
            <Button onClick={() => setSelected('new')}>+ {t('cms:heroes.new')}</Button>
          ) : null
        }
      />

      <div className="px-6 pb-8">
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(row) => row.id}
          label={t('cms:heroes.title')}
          total={result.data?.total ?? 0}
          page={page}
          onPageChange={setPage}
          sorting={sorting}
          onSortingChange={(next) => {
            setSorting(next);
            setPage(1);
          }}
          isLoading={result.isLoading}
          isFetching={result.isFetching}
          isError={result.isError}
          onRetry={() => void result.refetch()}
          onRowClick={setSelected}
          rowLabel={(row) => t('cms:table.openRow', { name: row.name })}
          sortableIds={SORT_IDS}
          selectedRowId={selected && selected !== 'new' ? selected.id : null}
          csv={{
            filename: 'akapella-heroes.csv',
            columns: csvColumns,
            // The whole filtered set, not the page on screen (CMS-22).
            rows: (onProgress) =>
              fetchAllRowsResult(
                (exportPage, pageSize) =>
                  api.cmsHeroes({ query: { ...query, page: exportPage, pageSize } }),
                { onProgress },
              ),
          }}
          toolbar={
            <>
              <Input
                className="w-64"
                placeholder={t('cms:heroes.searchPlaceholder')}
                aria-label={t('cms:heroes.searchPlaceholder')}
                value={filters.q}
                onChange={(e) => update({ q: e.target.value })}
              />
              <span className="flex items-center gap-1.5">
                <Label htmlFor="f-hero-type">{t('cms:heroes.type')}</Label>
                <Select
                  id="f-hero-type"
                  className="w-40"
                  value={filters.type}
                  onChange={(e) => update({ type: e.target.value as HeroType | '' })}
                >
                  <option value="">{t('cms:songs.filters.all')}</option>
                  <option value="song">{t('cms:heroes.typeSong')}</option>
                  <option value="playlist">{t('cms:heroes.typePlaylist')}</option>
                </Select>
              </span>
              <span className="flex items-center gap-1.5">
                <Label htmlFor="f-hero-status">{t('cms:heroes.status')}</Label>
                <Select
                  id="f-hero-status"
                  className="w-40"
                  value={filters.status}
                  onChange={(e) => update({ status: e.target.value as PublishStatus | '' })}
                >
                  <option value="">{t('cms:songs.filters.all')}</option>
                  <option value="draft">{t('cms:status.draft')}</option>
                  <option value="published">{t('cms:status.published')}</option>
                </Select>
              </span>
              <Button variant="link" size="sm" onClick={() => update(EMPTY_HERO_FILTERS)}>
                {t('cms:songs.clearFilters')}
              </Button>
            </>
          }
        />
      </div>

      {selected && canEdit && (
        <HeroDrawer
          key={selected === 'new' ? 'new' : `${selected.id}:${selected.updatedAt}`}
          hero={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
